import type { ErrorCode } from './errors';

/**
 * Shared error-display helper (used by apps/admin, apps/dashboard, apps/scanner, apps/checkout).
 * Every app's `request()`/API-client error class has slightly different field names
 * (`status` vs `httpStatus`, etc) for historical reasons, so this takes a loosely-typed, already-
 * normalized shape rather than importing each app's own class - callers do a one-line adapter at
 * the call site, e.g. `parseApiError({code: e.code, message: e.message, details: e.details,
 * requestId: e.requestId, httpStatus: e.status ?? e.httpStatus})`.
 *
 * The whole point: turn "Failed" into "what failed, why, and what you can do about it" - per the
 * user's hard rule against generic error messages. Never includes anything the server didn't
 * already decide was safe to show (stack traces/SQL/paths/secrets never reach this layer at all -
 * the server-side contract, apps/api/src/middleware/errorHandler.ts, is what actually enforces
 * that; this just renders whatever the server already sanitized).
 */

export type ApiErrorKind =
  | 'validation'
  | 'permission'
  | 'reauth'
  | 'conflict'
  | 'not_found'
  | 'rate_limited'
  | 'network'
  | 'server'
  | 'other';

export interface ApiFieldError {
  field: string;
  message: string;
}

export interface NormalizedApiError {
  code?: ErrorCode | string;
  message: string;
  details?: Record<string, unknown>;
  requestId?: string;
  httpStatus?: number;
}

export interface ParsedApiError {
  kind: ApiErrorKind;
  /** Short, user-facing category label - e.g. "Check the highlighted fields", "You don't have
   * permission to do this", "Confirm it's you to continue". */
  title: string;
  /** The main one-line explanation. For `validation`, this is a summary ("3 fields need
   * attention") - the field-by-field detail is in `fieldErrors`, meant to render next to each
   * input via `fieldError()` below, not repeated in this message. */
  message: string;
  fieldErrors: ApiFieldError[];
  /** Only set when `kind === 'permission'`. */
  missingPermission?: string;
  requestId?: string;
}

const KIND_TITLES: Record<ApiErrorKind, string> = {
  validation: 'Check the highlighted fields',
  permission: "You don't have permission to do this",
  reauth: "Confirm it's you to continue",
  conflict: "That didn't work because of a conflict",
  not_found: "That couldn't be found",
  rate_limited: 'Slow down a little',
  network: "Couldn't reach the Loctopus API",
  server: 'Something went wrong on our end',
  other: "That didn't work",
};

function kindFor(error: NormalizedApiError): ApiErrorKind {
  if (error.details?.reauthRequired === true) return 'reauth';
  switch (error.code) {
    case 'validation_failed':
      return 'validation';
    case 'forbidden':
    case 'tenant_mismatch':
      return 'permission';
    case 'conflict':
    case 'idempotency_conflict':
    case 'inventory_unavailable':
      return 'conflict';
    case 'not_found':
      return 'not_found';
    case 'rate_limited':
      return 'rate_limited';
    case 'internal_error':
    case 'provider_error':
    case 'payment_failed':
      return 'server';
    default:
      if (error.httpStatus && error.httpStatus >= 500) return 'server';
      return 'other';
  }
}

/** Zod's `issues` array shape, as put into `details.issues` by
 * apps/api/src/middleware/errorHandler.ts's ZodError branch. */
function extractFieldErrors(details: Record<string, unknown> | undefined): ApiFieldError[] {
  const issues = details?.issues;
  if (!Array.isArray(issues)) return [];
  const out: ApiFieldError[] = [];
  for (const issue of issues) {
    if (!issue || typeof issue !== 'object') continue;
    const path = (issue as { path?: unknown[] }).path;
    const message = (issue as { message?: unknown }).message;
    if (typeof message !== 'string') continue;
    const field = Array.isArray(path) && path.length > 0 ? path.join('.') : '(form)';
    out.push({ field, message });
  }
  return out;
}

/** The one shared error-handling helper. Pass it a normalized error (see `NormalizedApiError`),
 * get back something every app's shared `<ErrorSummary>` component (packages/ui) can render, and
 * every form's `<FormField error={fieldError(parsed, 'name')}>` can read from directly. */
export function parseApiError(error: NormalizedApiError): ParsedApiError {
  const kind = kindFor(error);
  const fieldErrors = kind === 'validation' ? extractFieldErrors(error.details) : [];
  const missingPermission =
    kind === 'permission' && typeof error.details?.permission === 'string'
      ? error.details.permission
      : undefined;

  let message = error.message;
  if (kind === 'validation' && fieldErrors.length > 0) {
    message =
      fieldErrors.length === 1
        ? `1 field needs attention: ${fieldErrors[0]!.field}.`
        : `${fieldErrors.length} fields need attention: ${fieldErrors.map((f) => f.field).join(', ')}.`;
  }

  return {
    kind,
    title: KIND_TITLES[kind],
    message,
    fieldErrors,
    missingPermission,
    requestId: error.requestId,
  };
}

/**
 * For a non-JSON / unparseable failure (network error, a 5xx with no body, a proxy error page) -
 * the UI must still handle this gracefully rather than crash trying to read `.error.message` off
 * something that isn't the expected envelope.
 *
 * Owner-reported real problem (not a developer, Windows, no `pnpm`): every app used to show the
 * browser's own raw `TypeError` text here - "Couldn't reach the Loctopus API: Failed to fetch" -
 * which means nothing to someone who doesn't know what "fetch" is. This is now a plain-language
 * message telling them exactly what to do, plus (when the caller passes it) the address the app
 * actually tried to reach, so a request ID is never implied - there isn't one, the request never
 * reached the server at all.
 */
export function parseNetworkOrUnknownError(raw: unknown, apiBaseUrl?: string): ParsedApiError {
  const addressNote = apiBaseUrl ? ` (tried to reach: ${apiBaseUrl})` : '';
  const message =
    'The Loctopus server is not running or not reachable. If you are testing locally, ' +
    'double-click START.cmd in the project folder and reload this page.' +
    addressNote;
  return {
    kind: 'network',
    title: KIND_TITLES.network,
    message,
    fieldErrors: [],
  };
}

/** Looks up ONE field's message out of a parsed validation error - what
 * `<FormField error={...}>` should be passed for that specific input. Field names must match the
 * API's zod schema's own key names (dot-path for nested, e.g. "address.city"). */
export function fieldError(
  parsed: ParsedApiError | null | undefined,
  field: string,
): string | undefined {
  return parsed?.fieldErrors.find((f) => f.field === field)?.message;
}
